/**
 * useFeatureFlag — minimal client-side feature flag check.
 *
 * Phase 2 (ADR-103 Addendum 2): returns the flag state for the current
 * tenant. The full per-tenant flag UI + backend `/auth/me` `features[]`
 * surfacing is a separate epic (D4, ~5 SP). This hook is the integration
 * point for that work — when the backend exposes flags, only this file
 * changes; call-sites stay unchanged.
 *
 * Current behaviour (intentional dev-mode default):
 *   - dev/demo environments: default ON
 *   - prod: default OFF
 *
 * The backend `RentalController.create` separately enforces the flag with
 * HTTP 403, so a client-side bypass cannot grant unauthorized access — this
 * hook only governs UI affordance (panel visibility).
 */

export type FeatureFlagName =
  | 'BERLES_MULTI_PACKAGE'
  // E-105 3-oszlopos rental wizard V2 layout — `EquipmentPackageStepV2`.
  // ADR-103 Amendment-1 ratified Sprint 26 W1 D-4 (panel-content swap).
  // Sprint 26 W1 D-5 flip → REVERT (V2 NEM functional, party-mode discovery)
  // → B build add-equipment picker + i18n complete + functional smoke RWV2-03
  // + P4 re-flip (Javo scope-ratify-reversal: V2 mockup-trail Phase 1 prio,
  // mockup #1/3-7 Sprint 26 W2+ Amendment-2 epic per Q-T017).
  // Current state: dev/demo ON by default via `isDevHost()`, prod OFF.
  // localStorage `feature.rentalWizardV2` ('true' | 'false') overrides
  // env-default for ad-hoc opt-in (prod tester) or opt-out (dev debug).
  | 'RENTAL_WIZARD_V2_LAYOUT'
  // ADR-103 Amendment-4 (RATIFIED 2026-06-01): single-route + step-state
  // step-wizard a V2 single-page layout FÖLÖTT (háromállapotú coexistence:
  // legacy accordion / V2 single-page / V3 step-wizard). KÜLÖN flag a
  // regresszió-lock egyértelműségéért (NEM a V2-flag bővítése — D5).
  // ⚠️ DEFAULT-ON FLIP (2026-06-03, Javo-ratify „A"): a flip-gate (d) measured-MET
  // (CARD/MyPOS no-404 + pure-DRAFT, `BER2026-00959` bilateral DB-verify) →
  // dev/demo default ON, prod OFF (mint a V2). localStorage override megtartva
  // (`feature.rentalStepWizard` 'true'|'false'). CASH-only default-ON: a CARD-ág
  // graceful-FAILED `MYPOS_ENABLED!=true` mellett (MyPOS-sandbox külön deferred
  // sztori). Reverzibilis (ADR-103 Am-4 konform, flag marad — csak a default vált).
  | 'RENTAL_STEP_WIZARD'
  // Step-2 gép-detail-editor paradigma (v3-proto, 2026-06-05).
  // Slice-1 váz: bal rail (kosárgépek) + közép aktív-gép-detail shell
  // (fejléc + csomag-választó + PackageContentBreakdown reuse) + jobb kalk
  // placeholder. DEFAULT OFF mindenhol — localStorage override opt-in:
  // `feature.rentalStep2DetailEditor` 'true'|'false'.
  | 'RENTAL_STEP2_DETAIL_EDITOR'
  // 6-tab munkalap-detail layout + perzisztens cost-summary rail (2026-06-07).
  // Brand-new untested layout — DEFAULT OFF mindenhol (NEM isDevHost).
  // localStorage opt-in: `feature.worksheetDetailTabs` 'true'|'false'.
  | 'WORKSHEET_DETAIL_TABS';

const PROD_HOST_PATTERN = /^kgc\.mflerp\.com$/;

function isDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  return !PROD_HOST_PATTERN.test(window.location.hostname);
}

export function useFeatureFlag(flag: FeatureFlagName): boolean {
  // Stop-gap: per-environment default until /auth/me exposes tenant features.
  // ADR-103 Addendum 2 D4 will replace with tenant-scoped lookup.
  switch (flag) {
    case 'BERLES_MULTI_PACKAGE':
      return isDevHost();
    case 'RENTAL_WIZARD_V2_LAYOUT': {
      // DEFAULT ON (prod-flip 2026-06-16, Javo prod-ON-confirm) — a kétszintű proto-paritás-gate
      // ZÖLD: (1) RTL strukturált-parity Q-J014 #404 (7/7 a pinnelt v3-proto fixture ellen) +
      // (2) dev4-browser layout-gate (e2e/proto-layout/wizard-flip-layout.e2e.ts, 7/7 +
      // screenshot-baseline, dátum-drift flake-fix `59a52b44`). Reverzibilis (flag marad —
      // csak a default vált). Opt-out: `feature.rentalWizardV2` = 'false'. Tükrözi a
      // `WORKSHEET_DETAIL_TABS` prod-flip mintát.
      if (typeof window === 'undefined') return false;
      const stored = window.localStorage?.getItem('feature.rentalWizardV2');
      if (stored === 'true') return true;
      if (stored === 'false') return false;
      return true;
    }
    case 'RENTAL_STEP_WIZARD': {
      // DEFAULT-ON FLIP (Javo-ratify „A", 2026-06-03): dev/demo default ON,
      // prod OFF. localStorage override mindkét irányba (opt-in prod-tesztelő /
      // opt-out dev-debug). Tükrözi a `RENTAL_WIZARD_V2_LAYOUT` mintát.
      if (typeof window === 'undefined') return false;
      const stored = window.localStorage?.getItem('feature.rentalStepWizard');
      if (stored === 'true') return true;
      if (stored === 'false') return false;
      return isDevHost();
    }
    case 'RENTAL_STEP2_DETAIL_EDITOR': {
      // DEFAULT-ON FLIP (dev/demo default ON, prod OFF, 2026-06-06): a step-2
      // gép-detail-editor a dev/demo wizard alap-nézete. Prereq proto-paritás-gate
      // TELJESÍTVE: RTL strukturált-parity (#275) + dev4-browser layout-gate (#280)
      // ZÖLD. Reverzibilis (ADR-103 Am-4 konform, flag marad — csak a default vált).
      // Prod-default-ON = külön későbbi döntés (Zoli/Javo). Tükrözi a
      // `RENTAL_STEP_WIZARD` mintát. localStorage override mindkét irányba:
      // `feature.rentalStep2DetailEditor` 'true'|'false'.
      if (typeof window === 'undefined') return false;
      const stored = window.localStorage?.getItem('feature.rentalStep2DetailEditor');
      if (stored === 'true') return true;
      if (stored === 'false') return false;
      return isDevHost();
    }
    case 'WORKSHEET_DETAIL_TABS': {
      // DEFAULT ON (flip 2026-06-12, Javo csomag-GO b-tétel) — a kétszintű proto-paritás-gate
      // ZÖLD: (1) RTL strukturált-parity #419 (7/7 a pinnelt #417 fixture ellen) +
      // (2) dev4-browser layout-gate (e2e/proto-layout/worksheet-detail-layout.e2e.ts,
      // 4/4 + screenshot-baseline). Opt-out: `feature.worksheetDetailTabs` = 'false'.
      if (typeof window === 'undefined') return false;
      const stored = window.localStorage?.getItem('feature.worksheetDetailTabs');
      if (stored === 'true') return true;
      if (stored === 'false') return false;
      return true;
    }
  }
}
